// StudioWorkingCard — the assistant at work, as a card on the Studio canvas
// (pen B-04), pure: the task the running turn is on, named without a subject
// (WL-1: the chat's own live-activity label), and for how long. A turn that
// failed or was stopped shows why, then fades (the canvas's CSS; no timer drives state).
//
// Clicking it opens that conversation in the chat panel.

export { StudioWorkingCard };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useElapsedSinceMount, useNow } from "../../hooks/useNow.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { formatElapsed } from "../Chat/messageTime.js";
import { liveActivityLabel, workingLine } from "../Chat/toolStepLabels.js";
import { Icon } from "../Icon/Icon.js";
import { Mark, type MarkState } from "../Mark/Mark.js";
import type { StudioWork } from "../Studio/studioWork.js";
import "./StudioWorkingCard.css";

const THINKING: MarkState = { type: "thinking" };

// The clock: from the turn's recorded start when known, else from when this
// card appeared (a live message carries no timestamp yet).
const Elapsed = ({ startedAt }: { readonly startedAt?: number }): ReactNode => {
    const now = useNow(1000);
    const sinceMount = useElapsedSinceMount(1000);
    const ms = startedAt === undefined ? sinceMount : now - startedAt;
    return (
        <span className="bilda-studio-working__elapsed" aria-hidden="true">
            {formatElapsed(Math.max(0, ms))}
        </span>
    );
};

const StudioWorkingCard = ({
    work,
    onOpen,
}: {
    readonly work: StudioWork;
    readonly onOpen: () => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    // WL-1: the head names the task ("Creating a quiz…"), not the assistant.
    // One line; the full text on hover.
    const task =
        work.type === "working"
            ? i18n._(
                  workingLine(
                      liveActivityLabel(work.activity, (m) => i18n._(m)),
                  ),
              )
            : t`The assistant stopped`;
    return (
        <button
            type="button"
            className="bilda-studio-working"
            data-state={work.type}
            onClick={onOpen}
            aria-label={t`Open the conversation`}
        >
            <span className="bilda-studio-working__head">
                {work.type === "working" ? (
                    <Mark
                        state={THINKING}
                        size="sm"
                        className="bilda-studio-working__mark"
                    />
                ) : (
                    <Icon name="stopped" size="sm" />
                )}
                <span
                    className="bilda-studio-working__title"
                    title={task}
                    role={work.type === "working" ? "status" : undefined}
                >
                    {task}
                </span>
                <AiMarker variant="chip" />
            </span>
            {work.type === "working" ? (
                <span className="bilda-studio-working__step">
                    <Elapsed startedAt={work.startedAt} />
                </span>
            ) : (
                <span className="bilda-studio-working__step" role="status">
                    {work.reason}
                </span>
            )}
        </button>
    );
};
